'use client'

import React from 'react'

import { type VariantProps, cva } from 'class-variance-authority'
import { type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-1.5 border font-bold uppercase tracking-wide shrink-0 transition-colors',
  {
    variants: {
      variant: {
        default:
          'bg-accent-50 text-accent-700 border-accent-200 dark:bg-accent-900/30 dark:text-accent-400 dark:border-accent-800',
        primary:
          'bg-blue-600 text-white border-blue-600 shadow-sm dark:bg-blue-500 dark:border-blue-500',
        // Alias for primary - matches inline Badge "blue" variant
        blue: 'bg-blue-600 text-white border-blue-600 shadow-sm dark:bg-blue-500 dark:border-blue-500',
        success:
          'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
        warning:
          'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
        neutral:
          'bg-primary-100 text-primary-600 border-primary-200 dark:bg-primary-800 dark:text-primary-400 dark:border-primary-700',
        outline:
          'bg-transparent text-primary-500 border-primary-300 dark:text-primary-400 dark:border-primary-600',
        info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs rounded-md',
        md: 'px-2.5 py-1 text-xs rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
)

export interface BadgeV2Props
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  icon?: LucideIcon
}

/**
 * BadgeV2 - Pill-shaped status/category badge for solicitation detail pages
 *
 * Features:
 * - Multiple color variants for different semantic meanings
 * - Optional leading icon
 * - Size variants (sm/md)
 * - Dark mode support
 *
 * @example
 * ```tsx
 * <BadgeV2 variant="success" icon={CheckCircle}>Active</BadgeV2>
 * <BadgeV2 variant="warning">Presolicitation</BadgeV2>
 * <BadgeV2 variant="primary">SBIR</BadgeV2>
 * ```
 */
const BadgeV2: React.FC<BadgeV2Props> = ({
  children,
  variant,
  size,
  icon: Icon,
  className,
  ...props
}) => {
  return (
    <span
      className={twMerge(badgeVariants({ variant, size }), className)}
      {...props}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  )
}

BadgeV2.displayName = 'BadgeV2'

// ============================================================================
// Skeleton Sub-Component
// ============================================================================

interface BadgeSkeletonProps {
  size?: 'sm' | 'md'
  /** Approximate width - xs: 12, sm: 16, md: 20, lg: 24 (in tailwind units) */
  width?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
}

const skeletonSizeStyles = {
  sm: 'h-5 rounded-md',
  md: 'h-6 rounded-full',
}

const skeletonWidthStyles = {
  xs: 'w-12',
  sm: 'w-16',
  md: 'w-20',
  lg: 'w-24',
}

/**
 * BadgeV2.Skeleton - Loading skeleton matching badge dimensions
 *
 * @example
 * ```tsx
 * <BadgeV2.Skeleton width="md" />
 * <BadgeV2.Skeleton size="sm" width="lg" />
 * ```
 */
const BadgeSkeleton: React.FC<BadgeSkeletonProps> = ({
  size = 'md',
  width = 'md',
  className,
}) => {
  return (
    <div
      className={twMerge(
        'animate-pulse bg-primary-200 dark:bg-primary-700',
        skeletonSizeStyles[size],
        skeletonWidthStyles[width],
        className,
      )}
    />
  )
}

BadgeSkeleton.displayName = 'BadgeV2.Skeleton'

// Attach skeleton to BadgeV2
const BadgeV2WithSkeleton = BadgeV2 as typeof BadgeV2 & {
  Skeleton: typeof BadgeSkeleton
}
BadgeV2WithSkeleton.Skeleton = BadgeSkeleton

export type BadgeVariant = NonNullable<BadgeV2Props['variant']>

export { BadgeV2WithSkeleton as BadgeV2, badgeVariants }
export type { BadgeSkeletonProps }
